import { AnalysisResult } from '@shared-interfaces/shared-workflow';
import { allSameValue, atLeastOneValue } from '@shared-helper/helper-functions';

/**
 * Classify an aggregated `analysisSummary.shortAnswer` string (which may be a
 * full LLM-generated sentence) into one of: 'yes' | 'no' | 'tbc'.
 *
 * Previous implementations used plain `String.includes(...)` which caused
 * serious false positives — e.g. `includes('no')` matches inside common words
 * like "Informationen", "Kontrollmechanismen", "not", and `includes('nein')`
 * matches inside "keinen", "seinen", etc. This collapsed many real "Yes"
 * answers into "No" / "To be checked".
 *
 * Strategy:
 *   1. Trim/normalize the input.
 *   2. Match on the FIRST token / leading label (the LLM is prompted to start
 *      the sentence with the verdict — e.g. "Ja, ...", "Nein, ...",
 *      "Erfüllt.", "Nicht erfüllt — ...", "Teilweise erfüllt, ...",
 *      "Yes, the contract ...", "No, ...", "To be checked ...").
 *   3. Use word-boundary regexes to avoid substring collisions.
 *   4. Check the more specific labels ("nicht erfüllt", "teilweise erfüllt")
 *      BEFORE the generic "erfüllt" / "yes" / "ja".
 */
export type SummaryVerdict = 'yes' | 'no' | 'tbc';

const NOT_FULFILLED_RE = /\bnicht\s+erfüllt\b/i;
const PARTIALLY_FULFILLED_RE = /\bteilweise\b/i;
const TO_BE_CHECKED_RE = /\bzu\s+prüfen\b|\bto\s+be\s+checked\b|\bpartially\b/i;
const FULFILLED_RE = /\berfüllt\b/i;
const YES_RE = /\b(ja|yes)\b/i;
const NO_RE = /\b(nein|no)\b/i;

export function classifySummaryAnswer(raw: string | null | undefined): SummaryVerdict {
    if (!raw || typeof raw !== 'string') {
        return 'tbc';
    }

    const text = raw.trim();
    if (text.length === 0) {
        return 'tbc';
    }

    // Specific negative / partial labels first — they contain the word
    // "erfüllt" and would otherwise be matched by FULFILLED_RE.
    if (NOT_FULFILLED_RE.test(text)) {
        return 'no';
    }
    if (PARTIALLY_FULFILLED_RE.test(text) || TO_BE_CHECKED_RE.test(text)) {
        return 'tbc';
    }

    // Positive verdict: either "erfüllt" anywhere (now safe, "nicht erfüllt"
    // was handled above), or a leading/standalone "ja" / "yes".
    if (FULFILLED_RE.test(text) || YES_RE.test(text)) {
        return 'yes';
    }

    // Negative verdict: standalone "nein" / "no" (word-boundary, so it won't
    // match inside "keinen", "Informationen", "notwendig", etc.).
    if (NO_RE.test(text)) {
        return 'no';
    }

    return 'tbc';
}

/**
 * Classify ONE analysis question (all files + optional aggregated summary)
 * into 'yes' | 'no' | 'tbc'. Returns `null` for unanswered questions
 * (no summary and every file answer empty) so callers can skip them.
 *
 * This is the SINGLE source of truth for the per-question verdict. It is used by
 * the "Analysis results" chip counts, and by the 7-digit `activeStep` number that
 * feeds the dashboard contract tiles, so the two views can never disagree.
 *
 * - New schema: `analysisSummary.shortAnswer` (per-question aggregated answer).
 * - Old schema: aggregate the per-file answers.
 */
export function classifyQuestionResult(item: AnalysisResult): SummaryVerdict | null {
    if (item.analysisSummary?.shortAnswer) {
        return classifySummaryAnswer(item.analysisSummary.shortAnswer);
    }

    const fileAnswers = (item.files ?? []).map(f => {
        const raw = f.result?.['short_answer'] ?? f.shortAnswer ?? '';
        return typeof raw === 'string' ? raw : '';
    });
    if (fileAnswers.every(a => a.trim() === '')) {
        return null;
    }
    if (atLeastOneValue('Ja', item.files) || atLeastOneValue('Yes', item.files) || atLeastOneValue('erfüllt', item.files)) {
        return 'yes';
    }
    if (allSameValue('Nein', item.files) || allSameValue('No', item.files) || allSameValue('nicht erfüllt', item.files)) {
        return 'no';
    }
    return 'tbc';
}

export interface VerdictCounts {
    yes: number;
    no: number;
    tbc: number;
}

/** Count per-question verdicts, skipping unanswered questions. */
export function countQuestionVerdicts(results: AnalysisResult[] | null | undefined): VerdictCounts {
    const counts: VerdictCounts = { yes: 0, no: 0, tbc: 0 };
    (results ?? []).forEach(item => {
        const verdict = classifyQuestionResult(item);
        if (verdict) {
            counts[verdict]++;
        }
    });
    return counts;
}

/**
 * Build the 7-digit `activeStep` number from verdict counts.
 * Format: [overall][yes:2][no:2][tbc:2]
 * Overall: 2 = all yes, 3 = all no, 4 = mixed / to be checked.
 */
export function buildActiveStepNumber(counts: VerdictCounts): number {
    const answered = counts.yes + counts.no + counts.tbc;
    let firstDigit = 4;
    if (answered > 0 && counts.yes === answered) {
        firstDigit = 2;
    } else if (answered > 0 && counts.no === answered) {
        firstDigit = 3;
    }
    const pad = (n: number) => Math.min(n, 99).toString().padStart(2, '0');
    return parseInt(`${firstDigit}${pad(counts.yes)}${pad(counts.no)}${pad(counts.tbc)}`, 10);
}
